import { createContext, useContext, useState } from "react"

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { animated, useTransition } from "@react-spring/web"

import { cn } from "@/lib/utils"

type DialogContext = {
  open: boolean
  onLeaveAnimationFinish?: () => void
}
const DialogContext = createContext<DialogContext>({ open: false })

export const Root = ({
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange: externalOnOpenChange,
  onClose,
  ...props
}: DialogPrimitive.Root.Props & { onClose?: () => void }) => {
  const [open, setOpen] = useState(defaultOpen)
  const [animatingOut, setAnimatingOut] = useState(false)

  const isOpen = controlledOpen !== undefined ? controlledOpen : open

  return (
    <DialogContext.Provider
      value={{
        open: isOpen,
        onLeaveAnimationFinish: () => {
          setAnimatingOut(false)
          onClose?.()
        },
      }}
    >
      <DialogPrimitive.Root
        {...props}
        open={isOpen || animatingOut}
        onOpenChange={(value, e) => {
          externalOnOpenChange?.(value, e)
          if (!value) setAnimatingOut(true)
          setOpen(value)
        }}
      />
    </DialogContext.Provider>
  )
}

export const Content = ({
  children,
  className,
  overlayClassName,
  position = "right",
  title,
  ...props
}: DialogPrimitive.Popup.Props & {
  position?: "left" | "right" | "top"
  overlayClassName?: string
}) => {
  const { open, onLeaveAnimationFinish } = useContext(DialogContext)

  const transitionStyles = {
    right: {
      from: {
        opacity: 0,
        transform: "translateX(100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(100%)",
      },
    },
    left: {
      from: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
    },
    top: {
      from: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
      enter: { opacity: 1, transform: "translateY(0%)" },
      leave: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
    },
  }[position]

  const transitions = useTransition(open, {
    ...transitionStyles,
    config: {
      clamp: true,
      tension: 300,
    },
    onDestroyed(value) {
      if (value) onLeaveAnimationFinish?.()
    },
  })

  return (
    <>
      {transitions((styles, item) =>
        item ? (
          <DialogPrimitive.Portal>
            <DialogPrimitive.Backdrop
              id="overlay"
              className={cn(
                "fixed inset-0 isolate z-modal-overlay bg-black/50",
                overlayClassName,
              )}
              render={<animated.div style={{ opacity: styles.opacity }} />}
              forceRender
            />

            <DialogPrimitive.Popup
              render={
                <animated.div style={styles}>
                  <Title className="sr-only">{title}</Title>
                  {children}
                </animated.div>
              }
              className={cn(
                "fixed top-0 isolate z-modal h-screen w-fit overflow-auto bg-white md:w-11/12",
                {
                  "left-0": position === "left",
                  "right-0": position === "right",
                  "top-0": position === "top",
                },
                className,
              )}
              {...props}
            />
          </DialogPrimitive.Portal>
        ) : null,
      )}
    </>
  )
}

export const Title = DialogPrimitive.Title
export const Description = DialogPrimitive.Description
export const Trigger = DialogPrimitive.Trigger
export const Close = DialogPrimitive.Close
